/**
 * clinicianName.js — THE way the patient app names a clinician (s119, D530 app half; his rulings 2026-10-24;
 * copy approved 2026-10-24: claude/ORELLA_APP_CLINICIAN_TITLES_COPY_v1.md).
 *   "Dr." ONLY for MD, DO, DDS, DMD, DPM, OD   -> "Dr. Jane Smith" (Spanish: "Dra." when the doctor is female)
 *   everyone else                             -> "Jane Smith, NP"
 *   unknown credential                        -> "Jane Smith"
 * A port of the API's clinician_title / _credential_class (api/index.py, s118) and of the MD panel's utils/clinicianName.js.
 * The API's own rendering wins: `doctorTitle` (get_case / get_cases / notification data) is used as given.
 *
 * ★ NOT MEASURED KEEPS "Dr." (rules §1fc(b)): a doctor object with no readable credential (most of the app's doctor
 *   objects carry none) renders as before. Only a credential the app CAN read (NP, PA-C, ...) or the API's title moves a
 *   name off "Dr.".
 * ★ SPANISH PUTS AN ARTICLE BEFORE A TITLE, so three forms (all equal in English):
 *   clinician        "Dr. Jane Smith" / "Dra. Jane Smith" / "Jane Smith, NP"
 *   clinicianSubject "El Dr. Jane Smith" / "La Dra. Jane Smith" / "Jane Smith, NP"   (starts a sentence)
 *   clinicianOf      "del Dr. Jane Smith" / "de la Dra. Jane Smith" / "de Jane Smith, NP" (after "de")
 * Never write a new inline "Dr. ${...}" — scripts/check-clinician-titles.mjs fails the build on one.
 * Pure module (one import): the gates import it directly.
 */
import { getAppLanguage } from './appLanguage.js'

const DR = new Set(['MD', 'DO', 'DDS', 'DMD', 'DPM', 'OD'])
const PA = new Set(['PA', 'PA-C', 'PAC'])
const CASE = { PHD: 'PhD', AUD: 'AuD', PSYD: 'PsyD', PHARMD: 'PharmD', DRPH: 'DrPH', EDD: 'EdD', SCD: 'ScD', DSC: 'DSc' }

export function credentialTokens(raw) {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) raw = raw.text || raw.credential || raw.credentials || ''
  else if (Array.isArray(raw)) raw = raw.filter(Boolean).join(', ')
  raw = String(raw ?? '').trim()
  if (/^[[{"]/.test(raw)) {
    try { return credentialTokens(JSON.parse(raw)) } catch { /* plain text */ }
  }
  return raw.replace(/\./g, '').toUpperCase().split(/[,/;&]+|\s+/)
    .map(p => p.replace(/^[\s\-()]+|[\s\-()]+$/g, ''))
    .filter(p => p && p !== 'AND')
}

const label = (tokens) => tokens.slice(0, 2).map(t => CASE[t] || t).join(', ')

function classify(tokens) {
  if (!tokens.length) return { cls: 'unknown', label: '' }
  if (tokens.some(t => DR.has(t))) {
    const drt = tokens.filter(t => DR.has(t))
    return { cls: 'dr', label: label(drt.concat(tokens.filter(t => !DR.has(t)))) }
  }
  const pat = tokens.filter(t => PA.has(t))
  if (pat.length) return { cls: 'pa', label: label(pat) }
  const npt = tokens.filter(t => t === 'NP' || t.endsWith('NP') || t.includes('NP-') || ['APRN', 'CRNP', 'ARNP', 'APN'].includes(t))
  if (npt.length) return { cls: 'np', label: label(npt) }
  return { cls: 'other', label: label(tokens) }
}

export function credentialClass(doc) {
  try {
    const d = doc && typeof doc === 'object' ? doc : {}
    // A one-string name may carry its credential after a comma ("Jane Smith, NP" — the API's own title shape).
    const nameCred = !d.first_name && !d.firstName && typeof d.name === 'string' && d.name.includes(',') ? d.name.split(',').slice(1).join(',') : null
    const own = classify(credentialTokens(d.credentials || d.credential || d.doctorCredential || nameCred))
    const reg = classify(credentialTokens(d.credentials_text ?? d.credentialsText))
    let pick = own
    if (own.cls === 'unknown' || (own.cls === 'dr' && own.label === 'MD' && !['unknown', 'dr'].includes(reg.cls))) {
      pick = reg.cls !== 'unknown' ? reg : own
    }
    if (pick.cls === 'unknown') {
      let nc = d.nppes_check ?? d.nppesCheck
      if (typeof nc === 'string') { try { nc = JSON.parse(nc) } catch { nc = null } }
      const tax = String((nc && typeof nc === 'object' && nc.taxonomy) || d.taxonomy || '').toLowerCase()
      if (tax.includes('nurse practitioner')) return { cls: 'np', label: 'NP' }
      if (tax.includes('physician assistant')) return { cls: 'pa', label: 'PA' }
    }
    return pick
  } catch {
    return { cls: 'unknown', label: '' }
  }
}

const isFemale = (d) => /^f/i.test(String(d.doctorGender ?? d.gender ?? d.sex ?? '').trim())

function names(d) {
  let first = String(d.first_name ?? d.firstName ?? '').trim()
  let last = String(d.last_name ?? d.lastName ?? '').trim()
  if (!first && !last && d.name) {
    // One `name` string ("Jane Smith", "Dr. Jane Smith", "Jane Smith, NP"): never double the title or the credential.
    const parts = String(d.name).replace(/^dra?\.?\s+/i, '').replace(/,.*$/, '').trim().split(/\s+/)
    if (parts.length > 1) { last = parts.pop(); first = parts.join(' ') } else { last = parts[0] || ''; first = '' }
  }
  return { first, last }
}

/** The doctor's parts: { cls: 'dr' | 'named', title: 'Dr.' | 'Dra.' | '', full, short } in the given language. */
function parts(doc, style, lang) {
  const d = doc && typeof doc === 'object' ? doc : {}
  const es = lang === 'es'
  const sent = String(d.doctorTitle || '').trim()
  const { first, last } = names(d)
  const full = `${first} ${last}`.trim()
  if (!full && !sent) return null
  // The API's own title wins for the display style (as in the MD panel); other styles derive from it below.
  if (!full || (sent && style === 'display')) {
    // Only the API's title: "Dr. Jane Smith" (dr) or "Jane Smith, NP" / "Jane Smith" (named).
    if (/^dr\.?\s/i.test(sent)) {
      const rest = sent.replace(/^dr\.?\s+/i, '')
      const lastWord = rest.split(/\s+/).pop()
      return { cls: 'dr', title: es && isFemale(d) ? 'Dra.' : 'Dr.', body: style === 'short' ? lastWord : rest }
    }
    return { cls: 'named', title: '', body: style === 'short' ? sent.replace(/,.*$/, '') : sent }
  }
  let { cls, label: lab } = credentialClass(d)
  if (cls === 'unknown') cls = sent && !/^dr\.?\s/i.test(sent) ? 'named-api' : 'dr'
  if (cls === 'dr') {
    const title = es && isFemale(d) ? 'Dra.' : 'Dr.'
    if (style === 'short') return { cls: 'dr', title, body: last || first }
    if (style === 'formal' && lab) return { cls: 'dr', title, body: `${full}, ${lab}` }
    return { cls: 'dr', title, body: full }
  }
  if (cls === 'named-api') return { cls: 'named', title: '', body: style === 'short' ? full : sent }
  if (style === 'short' || !lab) return { cls: 'named', title: '', body: full }
  return { cls: 'named', title: '', body: `${full}, ${lab}` }
}

/**
 * style: 'display' "Dr. Jane Smith" | 'short' "Dr. Smith" | 'formal' "Dr. Jane Smith, MD"
 * (non-Dr.: display/formal "Jane Smith, NP", short "Jane Smith"). '' when there is no name.
 * lang defaults to the app language (utils/appLanguage).
 */
export function clinicianName(doc, style = 'display', lang = getAppLanguage()) {
  const p = parts(doc, style, lang)
  if (!p) return ''
  return p.title ? `${p.title} ${p.body}` : p.body
}

/** The three i18n forms (see the header). English: all three are clinicianName(doc, style). */
export function clinicianForms(doc, style = 'display', lang = getAppLanguage()) {
  const p = parts(doc, style, lang)
  if (!p) return { clinician: '', clinicianSubject: '', clinicianOf: '' }
  const clinician = p.title ? `${p.title} ${p.body}` : p.body
  if (lang !== 'es') return { clinician, clinicianSubject: clinician, clinicianOf: clinician }
  if (!p.title) return { clinician, clinicianSubject: clinician, clinicianOf: `de ${clinician}` }
  const fem = p.title === 'Dra.'
  return { clinician, clinicianSubject: `${fem ? 'La' : 'El'} ${clinician}`, clinicianOf: `${fem ? 'de la' : 'del'} ${clinician}` }
}

/**
 * s120 · "Send my case to <clinician>" (Dr. Rami 2026-10-28: name the clinician, not "this doctor" — NPs/PAs are not
 * doctors). English: "Dr. Jane Smith" / "Jane Smith, NP". Spanish carries the article the title needs:
 * "al Dr. …" / "a la Dra. …" / "a Jane Smith, NP". '' when there is no name.
 */
export function clinicianTo(doc, lang = getAppLanguage()) {
  const p = parts(doc, 'display', lang)
  if (!p) return ''
  const who = p.title ? `${p.title} ${p.body}` : p.body
  if (lang !== 'es') return who
  if (!p.title) return `a ${who}`
  return `${p.title === 'Dra.' ? 'a la' : 'al'} ${who}`
}

/**
 * The case's own clinician fields (API v243.393.0: doctorTitle / doctorCredential / doctorGender) laid over a doctor
 * object, so a screen that has both renders the API's answer. Only when `doc` IS the case's doctor (same id, or no id).
 */
export function withCaseClinician(doc, caseData) {
  const d = doc && typeof doc === 'object' ? doc : {}
  const c = caseData && typeof caseData === 'object' ? caseData : {}
  if (!c.doctorTitle && !c.doctorCredential && !c.doctorGender) return d
  const caseDocId = c.doctorId || c.doctor_id || c.sentToDoctor || c.sent_to_doctor || c.doctor?.id || null
  if (d.id && caseDocId && String(d.id) !== String(caseDocId)) return d
  return {
    ...d,
    doctorTitle: d.doctorTitle || c.doctorTitle || undefined,
    doctorCredential: d.doctorCredential || c.doctorCredential || undefined,
    doctorGender: d.doctorGender || c.doctorGender || undefined,
  }
}

/** A bare name string ("Smith", "Jane Smith", "Dr. Jane Smith") as a doctor object, for screens that only hold the text. */
export const clinicianFromText = (text) => ({ name: String(text || '').trim() })

/**
 * The i18n forms for a clinician known only from notification data: the API's own title (doctorTitle, API v243.393.0)
 * when it sent one, else the bare name ("Smith" / "Jane Smith"), which renders as before ("Dr. Smith").
 */
export const clinicianFormsFor = (title, name, style = 'display', lang = getAppLanguage()) =>
  clinicianForms(String(title || '').trim() ? { doctorTitle: String(title).trim() } : clinicianFromText(name), style, lang)
